import { Drawer, Flex, Header, IconButton, Text, useToast } from '@driftt/dds'
import { faClockRotateLeft } from '@driftt/dds.icons/regular/faClockRotateLeft'
import React, { forwardRef, useImperativeHandle, useRef, useState } from 'react'

import { formatTime } from 'utils/moment'

import { configByType } from './constants'
import { PromptContainer } from './PromptContainer'
import { TextareaWithButton } from './TextareaWithButton'
import { useValidations } from './useValidations'
import { Validation } from './Validation'
import {
  StyledInfoContainer,
  StyledScrollbarHiddenContainer,
} from './ValidationDrawer.styles'

export interface ValidationDrawerRef {
  validate: (prompt: string) => void
}

export interface ValidationDrawerProps {
  open: boolean
  onClose: () => void
  savedValidaton?: string
  type: keyof typeof configByType
  onAction: (prompt: string, replyGuidelines?: string) => void
  handleHistoryDrawer: () => void
}

const ValidationDrawer = forwardRef<ValidationDrawerRef, ValidationDrawerProps>(
  (
    { open, onClose, savedValidaton, type, onAction, handleHistoryDrawer },
    ref
  ) => {
    const toast = useToast()
    const [promptInput, setPromptInput] = useState('')
    const bottomRef = useRef<null | HTMLDivElement>(null)
    const config = configByType[type]
    const scrollToBottom = () => {
      if (bottomRef.current) {
        bottomRef.current.scrollIntoView({
          block: 'end',
          behavior: 'smooth',
        })
      }
    }
    const { validations, validate } = useValidations({
      type,
      onSuccess: () => {
        // Handle success
        setPromptInput('')
        scrollToBottom()
      },
      onError: () => {
        toast({
          variant: 'error',
          message: `Failed to validate ${config.title.toLowerCase()}.`,
        })
      },
    })

    useImperativeHandle(
      ref,
      () => ({
        validate,
      }),
      [validate]
    )

    React.useEffect(() => {
      scrollToBottom()
    }, [bottomRef])

    return (
      <Drawer.Root open={open} onClose={onClose}>
        <Drawer.Dialog>
          <div style={{ marginTop: '-7px' }}>
            <Drawer.Title
              title=""
              prefix={
                <Flex
                  direction="row"
                  alignItems="flex-start"
                  justifyContent="space-between"
                  fullWidth
                >
                  <Header variant="h3">{config.title}</Header>
                  <IconButton
                    icon={faClockRotateLeft}
                    onClick={handleHistoryDrawer}
                    title=""
                    css={{ marginRight: '-15px' }}
                  />
                </Flex>
              }
            />
          </div>

          <Drawer.Content>
            <StyledScrollbarHiddenContainer direction="column">
              <StyledInfoContainer>{config.info}</StyledInfoContainer>
              {savedValidaton && (
                <div>
                  <Text variant="bodyBold" css={{ marginBottom: '$1' }}>
                    Current {config.title.toLowerCase()}
                  </Text>
                  <PromptContainer cssStyles={{ marginBottom: '$3' }}>
                    {savedValidaton}
                  </PromptContainer>
                </div>
              )}
              {validations.map(
                (
                  { prompt, response, replyGuidelines, validationResponseDate },
                  i
                ) => (
                  <Validation
                    prompt={replyGuidelines || prompt}
                    response={response}
                    type={type}
                    responseDate={formatTime(validationResponseDate, 'hh:mm A')}
                    onUse={() => {
                      onClose()
                      onAction(prompt, replyGuidelines)
                    }}
                  />
                )
              )}

              <div ref={bottomRef}></div>
            </StyledScrollbarHiddenContainer>
          </Drawer.Content>
          <Drawer.Actions>
            <Flex direction="row" alignItems="center" width="100%">
              <TextareaWithButton
                buttonLabel="Send"
                placeholder={configByType[type].placeholder}
                value={promptInput}
                // disabled={isSending}
                // loading={isSending}
                onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
                  setPromptInput(e.target.value)
                }
                onClickButton={() => validate(promptInput)}
              />
            </Flex>
          </Drawer.Actions>
        </Drawer.Dialog>
      </Drawer.Root>
    )
  }
)

export { ValidationDrawer }
